import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import { DeleteOutline, EditOutlined } from '@mui/icons-material';
import resourceSharingService from '../../services/resourceSharingService';
import { usePermissions } from '../../context/PermissionsContext';
import { PARTNER_PERMISSIONS } from '../../constants/permissions';
import { SHARING_COPY } from '../../constants/partnerCopy';
import { describeApiError, mapApiError } from '../../utils/apiError';
import ResourceShareDialog from './ResourceShareDialog';
import { isPartnerShare, principalKindLabel, principalLabel } from './sharingLabels';
import styles from './ResourceSharing.module.scss';

// §6.7: partner shares are edited only by the partner's staff.
const EDIT_ERRORS = [
  { status: 403, match: /belongs to another partner/i, message: 'This share belongs to another partner.' },
  { status: 403, match: /Missing permission: partner_sharing/i, message: "You don't have permission to share across companies." },
];

const accessText = (resourceType, share) => {
  if (resourceType === 'composio_app') {
    return (share.options?.auth_mode || 'with_auth') === 'with_auth' ? "Owner's connection" : 'Each user connects';
  }
  return (share.permission || 'read') === 'write' ? 'Write' : 'Read';
};

// `children` arrives as null from the API for a share with no child shares, and
// a default parameter only fills in for `undefined` — so the default never
// applied and the dialog crashed on open. Coerce instead of defaulting.
const childSummary = (children) => {
  const list = Array.isArray(children) ? children : [];
  const folders = list.filter((item) => String(item.resource_type).startsWith('folder_')).length;
  const tools = list.filter((item) => ['composio_app', 'custom_tool'].includes(item.resource_type)).length;
  return [folders ? `${folders} folder${folders === 1 ? '' : 's'}` : '', tools ? `${tools} tool${tools === 1 ? '' : 's'}` : '']
    .filter(Boolean).join(', ');
};

/**
 * Who has access (Partner tier guide §6.6–§6.7). Rows are grouped into
 * "Within <company>" (in-company shares) and "Across companies — shared by
 * <partner>" (partner shares). Callable by the owner company's sharers and by
 * the owning partner's staff:
 *   - partner shares are editable only by partner staff; the owner company's
 *     admins see them read-only with just Revoke (their veto);
 *   - in-company shares are editable only from inside the owner's company.
 * `resource.ownerCompanyId` / `ownerCompanyName` identify the owner when it
 * isn't the viewer's own company.
 */
const ManageSharingDialog = ({ open, resource, onClose, onChanged }) => {
  const { hasPartner, ready, company, partner, isPartnerStaff } = usePermissions();
  const partnerName = partner?.name || 'your partner';
  const homeCompanyId = partner?.home_company_id ?? null;
  const canPartnerShares = ready && hasPartner(PARTNER_PERMISSIONS.SHARING);
  const ownerCompanyId = resource?.ownerCompanyId ?? null;
  const inOwnerCompany =
    ownerCompanyId == null || (company?.id != null && Number(ownerCompanyId) === Number(company.id));
  const withinName =
    resource?.ownerCompanyName ||
    (inOwnerCompany ? (isPartnerStaff ? partnerName : company?.name) : null) ||
    'the owner company';

  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [confirmRevokeId, setConfirmRevokeId] = useState(null);
  const [error, setError] = useState('');
  const [editShare, setEditShare] = useState(null);
  const [pendingPermission, setPendingPermission] = useState(null);
  // `${resource_type}:${resource_id}` → display name, resolved from the agent's
  // attachments so the child-share counts can show names on hover.
  const [attachmentNames, setAttachmentNames] = useState(null);

  const load = useCallback(async () => {
    if (!resource?.id || !resource?.type) return;
    setLoading(true);
    setError('');
    try {
      const result = await resourceSharingService.getResourceShares(resource.type, resource.id);
      setShares(Array.isArray(result?.shares) ? result.shares.filter((item) => !item.parent_share_id) : []);
    } catch (err) {
      setError(describeApiError(err, 'Could not load sharing access.'));
    } finally {
      setLoading(false);
    }
  }, [resource?.id, resource?.type]);

  useEffect(() => { if (open) load(); }, [open, load]);

  useEffect(() => {
    if (!open || resource?.type !== 'agent' || !resource?.id) {
      setAttachmentNames(null);
      return undefined;
    }
    let cancelled = false;
    resourceSharingService.getAgentAttachments(resource.id)
      .then((data) => {
        if (cancelled) return;
        const names = new Map();
        (data?.folders_dim1 || []).forEach((item) => names.set(`folder_dim1:${item.id}`, item.name));
        (data?.folders_dim3 || []).forEach((item) => names.set(`folder_dim3:${item.id}`, item.name));
        (data?.composio_apps || []).forEach((item) => names.set(`composio_app:${item.id}`, item.app_name));
        (data?.custom_tools || []).forEach((item) => names.set(`custom_tool:${item.id}`, item.name));
        setAttachmentNames(names);
      })
      .catch(() => { if (!cancelled) setAttachmentNames(null); });
    return () => { cancelled = true; };
  }, [open, resource?.type, resource?.id]);

  const childName = (child) => {
    const mapped = attachmentNames?.get(`${child.resource_type}:${child.resource_id}`);
    if (mapped) return mapped;
    const fromShare = child.resource?.name || child.resource?.app_name || child.resource_name;
    if (fromShare) return fromShare;
    const label = child.resource_type === 'composio_app'
      ? 'Integration'
      : child.resource_type === 'custom_tool'
      ? 'Custom tool'
      : 'Folder';
    return `${label} #${child.resource_id}`;
  };

  const childDetails = (children) => {
    const list = Array.isArray(children) ? children : [];
    const folders = list.filter((item) => String(item.resource_type).startsWith('folder_'));
    const tools = list.filter((item) => ['composio_app', 'custom_tool'].includes(item.resource_type));
    if (!folders.length && !tools.length) return null;
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {folders.length > 0 && (
          <span>
            Folders: {folders.map((item) => `${childName(item)} (${item.permission || 'read'})`).join(', ')}
          </span>
        )}
        {tools.length > 0 && <span>Tools: {tools.map((item) => childName(item)).join(', ')}</span>}
      </Box>
    );
  };

  const updatePermission = async (share, permission) => {
    setBusyId(share.id);
    try {
      await resourceSharingService.updateShare(share.id, { permission });
      setShares((items) => items.map((item) => item.id === share.id ? { ...item, permission } : item));
      onChanged?.();
    } catch (err) {
      setError(mapApiError(err, EDIT_ERRORS, 'Could not update access.'));
    } finally {
      setBusyId(null);
    }
  };

  // Folder read→write hands out add/remove rights on the owner's files — confirm
  // before applying (agents get the same confirm inside the Edit dialog).
  const requestPermissionChange = (share, permission) => {
    if (
      String(resource?.type || '').startsWith('folder_') &&
      (share.permission || 'read') === 'read' &&
      permission === 'write'
    ) {
      setPendingPermission({ share, permission });
      return;
    }
    updatePermission(share, permission);
  };

  const updateAuthMode = async (share, authMode) => {
    setBusyId(share.id);
    try {
      await resourceSharingService.updateShare(share.id, { options: { auth_mode: authMode } });
      setShares((items) => items.map((item) => item.id === share.id
        ? { ...item, options: { ...(item.options || {}), auth_mode: authMode } }
        : item));
      onChanged?.();
    } catch (err) {
      setError(mapApiError(err, EDIT_ERRORS, 'Could not update authentication mode.'));
    } finally {
      setBusyId(null);
    }
  };

  const revoke = async (share) => {
    setBusyId(share.id);
    try {
      await resourceSharingService.revokeShare(share.id);
      setShares((items) => items.filter((item) => item.id !== share.id));
      setConfirmRevokeId(null);
      onChanged?.();
    } catch (err) {
      setError(mapApiError(err, EDIT_ERRORS, 'Could not revoke access.'));
    } finally {
      setBusyId(null);
    }
  };

  // §6.6 grouping. Headings only appear once a partner share exists, so
  // company-only resources look exactly as before.
  const withinShares = shares.filter((share) => !isPartnerShare(share));
  const acrossShares = shares.filter(isPartnerShare);
  const grouped = acrossShares.length > 0;
  const groups = [
    { key: 'within', title: SHARING_COPY.groupWithin(withinName), items: withinShares },
    { key: 'across', title: SHARING_COPY.groupAcross(partnerName), items: acrossShares },
  ].filter((group) => group.items.length > 0);

  const renderAccessControl = (share) => {
    if (resource?.type === 'composio_app') {
      return (
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Authentication</InputLabel>
          <Select label="Authentication" value={share.options?.auth_mode || 'with_auth'} disabled={busyId === share.id} onChange={(event) => updateAuthMode(share, event.target.value)}>
            <MenuItem value="with_auth">Owner's connection</MenuItem>
            <MenuItem value="without_auth">Each user connects</MenuItem>
          </Select>
        </FormControl>
      );
    }
    if (resource?.type === 'agent') {
      // This dropdown controls the AGENT only — folder/tool access is edited per
      // attachment via the Edit action.
      return (
        <FormControl size="small" sx={{ minWidth: 250 }}>
          <InputLabel>Agent access</InputLabel>
          <Select label="Agent access" value={share.permission || 'read'} disabled={busyId === share.id} onChange={(event) => requestPermissionChange(share, event.target.value)}>
            <MenuItem value="read">Read (run &amp; chat)</MenuItem>
            <MenuItem value="write">Write (also edit configuration)</MenuItem>
          </Select>
        </FormControl>
      );
    }
    return (
      <FormControl size="small" sx={{ minWidth: 130 }}>
        <InputLabel>Access</InputLabel>
        <Select label="Access" value={share.permission || 'read'} disabled={busyId === share.id} onChange={(event) => requestPermissionChange(share, event.target.value)}>
          <MenuItem value="read">Read</MenuItem>
          <MenuItem value="write">Write</MenuItem>
        </Select>
      </FormControl>
    );
  };

  const renderRow = (share) => {
    const partnerRow = isPartnerShare(share);
    // Partner rows: only partner staff edit (§6.7). In-company rows: only from
    // inside the owner's company.
    const editable = partnerRow ? canPartnerShares : inOwnerCompany;
    // Revoke: partner staff, or the owner company's sharers (their veto).
    const revocable = partnerRow ? canPartnerShares || inOwnerCompany : inOwnerCompany;
    const summary = childSummary(share.children);
    const details = childDetails(share.children);
    const isConfirming = confirmRevokeId === share.id;
    return (
      <Box className={styles.shareRow} key={share.id}>
        <Box>
          <Typography sx={{ fontWeight: 700 }}>{principalLabel(share, { partnerName, homeCompanyId })}</Typography>
          <Tooltip title={details || ''} placement="bottom-start" arrow>
            <Typography variant="caption" color="text.secondary" sx={details ? { cursor: 'help', textDecorationLine: 'underline', textDecorationStyle: 'dotted' } : undefined}>
              {principalKindLabel(share)}
              {share.options?.memory_mode ? ` · ${share.options.memory_mode === 'shared' ? 'Shared conversation' : 'Private conversations'}` : ''}
              {summary ? ` · ${summary}` : ''}
            </Typography>
          </Tooltip>
        </Box>
        {editable ? (
          renderAccessControl(share)
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>{accessText(resource?.type, share)}</Typography>
        )}
        {isConfirming ? (
          <>
            <Button size="small" onClick={() => setConfirmRevokeId(null)} disabled={busyId === share.id}>Keep</Button>
            <Button size="small" color="error" variant="contained" onClick={() => revoke(share)} disabled={busyId === share.id}>
              {busyId === share.id ? 'Revoking…' : 'Confirm revoke'}
            </Button>
          </>
        ) : (
          <>
            {editable ? (
              <Button size="small" startIcon={<EditOutlined />} onClick={() => setEditShare(share)} disabled={busyId === share.id}>Edit</Button>
            ) : (
              <span />
            )}
            {revocable ? (
              <Button size="small" color="error" startIcon={<DeleteOutline />} onClick={() => setConfirmRevokeId(share.id)}>Revoke</Button>
            ) : (
              <span />
            )}
          </>
        )}
        {isConfirming && partnerRow && !canPartnerShares && (
          <Alert severity="warning" sx={{ gridColumn: '1 / -1' }}>
            {SHARING_COPY.revokeAsOwner(resource?.name || 'this resource', partnerName)}
          </Alert>
        )}
        {isConfirming && resource?.type === 'agent' && (
          <Alert severity="warning" sx={{ gridColumn: '1 / -1' }}>This also removes access to folders and tools shared with this agent.</Alert>
        )}
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ className: styles.dialogPaper }}>
      <DialogTitle className={styles.dialogTitle}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 750 }}>Manage sharing</Typography>
          <Typography variant="body2" color="text.secondary">{resource?.name}</Typography>
        </Box>
      </DialogTitle>
      <DialogContent className={styles.content}>
        {loading ? <Box sx={{ minHeight: 180, display: 'grid', placeItems: 'center' }}><CircularProgress /></Box> : (
          <Box className={styles.section}>
            {shares.length === 0 ? (
              <Typography color="text.secondary">This resource is not currently shared.</Typography>
            ) : (
              <>
                {groups.map((group) => (
                  <Box key={group.key} sx={{ display: 'flex', flexDirection: 'column' }}>
                    {grouped && (
                      <Typography className={styles.sectionTitle} sx={{ mt: group.key === 'across' ? 2 : 0 }}>
                        {group.title}
                      </Typography>
                    )}
                    {group.items.map(renderRow)}
                  </Box>
                ))}
              </>
            )}
          </Box>
        )}
        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions className={styles.actions}><Button onClick={onClose}>Close</Button></DialogActions>
      {/* Edit reopens the standard share dialog prefilled from this row; the
          backend upserts on POST /shares, so re-saving is safe. */}
      <ResourceShareDialog
        open={Boolean(editShare)}
        resource={resource}
        initialShare={editShare}
        onClose={() => setEditShare(null)}
        onSuccess={() => { setEditShare(null); load(); onChanged?.(); }}
      />

      {/* Folder read→write confirmation (inline dropdown path). */}
      <Dialog open={Boolean(pendingPermission)} onClose={() => setPendingPermission(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Grant write access?</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            Recipients gain add/remove rights on the owner's folder files. Changes they make are visible to everyone with access.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingPermission(null)}>Go back</Button>
          <Button
            variant="contained"
            onClick={() => {
              const { share, permission } = pendingPermission;
              setPendingPermission(null);
              updatePermission(share, permission);
            }}
          >
            Grant write
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default ManageSharingDialog;
